<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { relativeDayTimestamp } from 'shared/helpers/timeHelper';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const agents = useMapGetter('agents/getAgents');

const isExpanded = ref(false);
const isAdding = ref(false);
const form = reactive({ title: '', dueAt: '', assigneeId: '' });

const tasks = computed(() =>
  getters['tasks/getForConversation'].value(props.conversationId)
);
const pendingTasks = computed(() =>
  tasks.value.filter(task => task.status === 'pending')
);

const toUnixTime = isoString =>
  isoString ? Math.floor(new Date(isoString).getTime() / 1000) : null;

const isOverdue = task =>
  task.status === 'pending' &&
  task.due_at &&
  new Date(task.due_at) < new Date();

const statusColorClass = task => {
  if (task.status === 'done') return 'bg-n-teal-9';
  if (task.status === 'cancelled') return 'bg-n-slate-8';
  return isOverdue(task) ? 'bg-n-ruby-9' : 'bg-n-amber-9';
};

const fetchTasks = () => {
  if (props.conversationId) {
    store.dispatch('tasks/get', props.conversationId);
  }
};

const resetForm = () => {
  form.title = '';
  form.dueAt = '';
  form.assigneeId = '';
  isAdding.value = false;
};

const createTask = async () => {
  if (!form.title.trim()) return;
  try {
    await store.dispatch('tasks/create', {
      conversationId: props.conversationId,
      title: form.title,
      dueAt: form.dueAt || null,
      assigneeId: form.assigneeId || null,
    });
    resetForm();
  } catch (error) {
    useAlert(t('CONVERSATION.TASKS.CREATE_ERROR'));
  }
};

const completeTask = async task => {
  try {
    await store.dispatch('tasks/update', {
      conversationId: props.conversationId,
      id: task.id,
      status: 'done',
    });
  } catch (error) {
    useAlert(t('CONVERSATION.TASKS.UPDATE_ERROR'));
  }
};

const deleteTask = async id => {
  try {
    await store.dispatch('tasks/delete', { conversationId: props.conversationId, id });
    useAlert(t('CONVERSATION.TASKS.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.TASKS.DELETE_ERROR'));
  }
};

onMounted(fetchTasks);
watch(() => props.conversationId, fetchTasks);
</script>

<template>
  <div class="px-4 pt-2">
    <button
      class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11 hover:text-n-iris-9"
      @click="isExpanded = !isExpanded"
    >
      <i class="i-lucide-check-square size-3.5" />
      {{ t('CONVERSATION.TASKS.LIST.TITLE') + ` (${pendingTasks.length})` }}
      <i
        class="i-lucide-chevron-down size-3.5 transition-transform"
        :class="{ 'rotate-180': isExpanded }"
      />
    </button>
    <div v-if="isExpanded" class="flex flex-col gap-1.5 mt-2 mb-1">
      <div
        v-for="task in tasks"
        :key="task.id"
        class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-n-solid-1 border border-n-weak text-xs"
      >
        <span class="rounded-full size-2 shrink-0" :class="statusColorClass(task)" />
        <span class="flex-1 truncate text-n-slate-12" :class="{ 'line-through text-n-slate-9': task.status === 'done' }">
          {{ task.title }}
        </span>
        <span v-if="task.due_at" class="text-n-slate-10 shrink-0">
          {{ relativeDayTimestamp(toUnixTime(task.due_at)) }}
        </span>
        <button
          v-if="task.status === 'pending'"
          v-tooltip.top="t('CONVERSATION.TASKS.LIST.COMPLETE_TOOLTIP')"
          class="text-n-slate-10 hover:text-n-teal-11 shrink-0"
          @click="completeTask(task)"
        >
          <i class="i-lucide-check size-3.5" />
        </button>
        <button
          v-tooltip.top="t('CONVERSATION.TASKS.LIST.DELETE_TOOLTIP')"
          class="text-n-slate-10 hover:text-n-ruby-11 shrink-0"
          @click="deleteTask(task.id)"
        >
          <i class="i-lucide-x size-3.5" />
        </button>
      </div>

      <div v-if="isAdding" class="flex flex-col gap-1.5 px-2.5 py-2 rounded-lg bg-n-solid-1 border border-n-weak">
        <input
          v-model="form.title"
          type="text"
          class="reply-composer--input"
          :placeholder="t('CONVERSATION.TASKS.FORM.TITLE_PLACEHOLDER')"
          @keyup.enter="createTask"
        />
        <div class="flex items-center gap-2">
          <input v-model="form.dueAt" type="datetime-local" class="reply-composer--input flex-1" />
          <select v-model="form.assigneeId" class="reply-composer--input flex-1">
            <option value="">{{ t('CONVERSATION.TASKS.FORM.UNASSIGNED') }}</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
        </div>
        <div class="flex items-center justify-end gap-2">
          <button class="text-xs text-n-slate-10" @click="resetForm">
            {{ t('CONVERSATION.TASKS.FORM.CANCEL') }}
          </button>
          <button class="text-xs font-medium text-n-iris-9" @click="createTask">
            {{ t('CONVERSATION.TASKS.FORM.SUBMIT') }}
          </button>
        </div>
      </div>
      <button
        v-else
        class="flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-iris-9"
        @click="isAdding = true"
      >
        <i class="i-lucide-plus size-3.5" />
        {{ t('CONVERSATION.TASKS.LIST.ADD') }}
      </button>
    </div>
  </div>
</template>
